import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import { encodeHTML } from '@recruitee/utils';

import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { SpinnerComponent } from '../../common/spinner/spinner.component';
import { ScrollableDirective } from '../../core/overlay/scroll/scrollable.directive';
import { Suggestion } from './typeahead.service';

export interface SuggestionItemContext<S = Suggestion> {
  highlighted: string | null;
  suggestion: S;
  label: string;
  select: (suggestion: S) => void;
}

@Component({
  selector: 'rt-typeahead-suggestions',
  styleUrls: ['./typeahead-suggestions.component.less'],
  templateUrl: './typeahead-suggestions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DropdownItemComponent,
    NgFor,
    NgIf,
    NgTemplateOutlet,
    ScrollableDirective,
    SpinnerComponent,
  ],
})
export class TypeaheadSuggestionsComponent {
  @Input() public stateName: 'HIDDEN_STATE' | 'LOADING_STATE' | 'EMPTY_STATE' | 'SUGGESTIONS_STATE';
  @Input() public stateValue: string | null;
  @Input() public suggestions: Suggestion[];
  @Input() public highlighted: string;
  @Input() public maxHeight: string = '300px';
  @Input() public minHeight: string = 'auto';
  @Input() public limit: number = 20;
  @Input() public page: number = 1;
  @Input() public wrapBody: boolean = true;
  @Input() public suggestionItemTemplate: TemplateRef<SuggestionItemContext>;
  @Input() public suggestionListTemplate: TemplateRef<{
    suggestions: Suggestion[];
    highlighted: string | null;
  }>;
  @Input() public spinnerTemplate?: TemplateRef<any>;

  @Output()
  public suggestionSelect: EventEmitter<Suggestion> = new EventEmitter();

  public suggestionsToDisplay: Suggestion[] = [];

  constructor(private cdRef: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.suggestions) {
      this.page = 1;
      this.suggestionsToDisplay = changes.suggestions.currentValue.slice(0, this.limit * this.page);
    }
  }

  public trackSuggestion(index: number, suggestion: Suggestion) {
    return suggestion.id;
  }

  public highlightValue(value: string, query: string | null): string {
    if (query && query?.length > 0) {
      return value.split(query).map(encodeHTML).join(`<span class="mark">${query}</span>`);
    }
    return encodeHTML(value);
  }

  public onReachEnd(): void {
    if (this.canLoadMore()) {
      this.loadMore();
    }
  }

  private loadMore(): void {
    this.page = this.page + 1;
    this.suggestionsToDisplay = this.suggestions.slice(0, this.limit * this.page);
    this.cdRef.detectChanges();
  }

  private canLoadMore(): boolean {
    return this.limit * this.page < this.suggestions.length;
  }
}
